import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgDialog,
  IgIconButton,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { ExperienceDto, OrganizationDto } from "@intelligems/ig-types";
import { Check } from "@mui/icons-material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Grid } from "@mui/material";

import { getDialog, isDialogOpen, setDialog } from "../../store/slices/global";
import theme from "../../theme";
import { copyToClipboard } from "../../utils/copyToClipboard";
import { generatePreviewLink } from "../../utils/generatePreviewLink";
import openNewTab from "../../utils/openNewTab";

interface PreviewExperimentProps {
  experience: ExperienceDto;
  organization: OrganizationDto;
}
export const PreviewExperience = ({
  experience,
  organization,
}: PreviewExperimentProps) => {
  const dispatch = useDispatch();
  const isOpen = useSelector(isDialogOpen("previewExperiment"));
  const dialog = useSelector(getDialog("previewExperiment"));
  const props = dialog.props as {
    id: string;
    path?: string;
    themeId?: string;
  };

  const handleClose = () => {
    dispatch(setDialog({ dialog: "previewExperiment", open: false }));
  };
  const previewLink = useMemo(() => {
    return generatePreviewLink({
      organization,
      experience,
      previewThemeId: props?.themeId,
    });
  }, [organization, experience, props?.themeId]);

  const [copyClicked, setCopyClicked] = useImmer(false);

  const onPreviewLinkCopy = async () => {
    await copyToClipboard(previewLink);
    setCopyClicked(true);
    setTimeout(() => setCopyClicked(false), 2000);
  };

  return (
    <IgDialog
      title={
        <Grid
          container
          direction={"column"}
          justifyContent={"center"}
          alignItems={"center"}
          textAlign={"center"}
        >
          <Grid
            container
            direction={"row"}
            item
            justifyContent={"center"}
            alignItems={"center"}
            textAlign={"center"}
          >
            <IgTypography variant={"h5"}>
              Open Preview{" "}
              <IgTooltip
                title={
                  copyClicked ? "Copied Preview Link" : "Copy Preview Link"
                }
              >
                <IgIconButton
                  onClick={() => {
                    void onPreviewLinkCopy();
                  }}
                >
                  {copyClicked ? (
                    <Check
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.success.main,
                      }}
                    />
                  ) : (
                    <ContentCopyIcon
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.secondary.main,
                      }}
                    />
                  )}
                </IgIconButton>
              </IgTooltip>
            </IgTypography>
          </Grid>
          <Grid item>
            <IgTypography igStyle={"semibold"}>{experience.name}</IgTypography>
          </Grid>
        </Grid>
      }
      handleClose={handleClose}
      showDialog={isOpen}
      hideActions={true}
      showCloseIcon={true}
      dialogHeight={"fit-content"}
      subtitle={
        <Grid item container justifyContent={"center"} direction={"column"}>
          <IgButton
            variant={"contained"}
            sx={{ marginTop: "10px", fontSize: "14px" }}
            onClick={() => window.open(previewLink, "_blank")}
          >
            Open desktop preview
          </IgButton>
          <IgButton
            variant={"outlined"}
            sx={{ marginY: "10px", fontSize: "14px" }}
            onClick={() => {
              handleClose();
              dispatch(
                setDialog({
                  dialog: "previewTestGroupCode",
                  open: true,
                  props: {
                    id: props?.id,
                    themeId: props?.themeId,
                    path: props?.path,
                  },
                })
              );
            }}
          >
            Get QR code for mobile
          </IgButton>
        </Grid>
      }
    ></IgDialog>
  );
};
